import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Group, Text, createStyles } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconStarFilled } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import useProducts from 'hooks/product/useProducts';
import { CellProps } from 'react-table';
import { ProductRetrieve } from 'types';

import ModalFeesAndMiscProductsDelete from './ModalFeesAndMiscProductsDelete';

const useStyles = createStyles(theme => ({
  tableRow: {
    cursor: 'pointer',
  },
  tableRowSelected: {
    background: theme.fn.rgba(theme.colors.kiloBlue[2], 0.5),
    cursor: 'pointer',
  },
}));

const FeesAndMiscProductsTable = ({
  search,
  orderByFavorites,
  openDrawer,
  setProductId,
  cartItems,
  setCartItems,
  productMutatePerm = true,
  invoiceCreatePerm = true,
  bgc = 'white',
}: {
  search: string;
  orderByFavorites: boolean;
  openDrawer: () => void;
  setProductId: (productId: string) => void;
  cartItems: ProductRetrieve[];
  setCartItems: React.Dispatch<React.SetStateAction<ProductRetrieve[]>>;
  productMutatePerm?: boolean;
  invoiceCreatePerm?: boolean;
  bgc?: 'white' | 'gray';
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const { formatCurrency } = useLocalization();

  const [selectedProduct, setSelectedProduct] = useState<ProductRetrieve | null>(null);
  const [isDeleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const [tableData, setTableData] = useState<ProductRetrieve[]>([]);
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const favoriteOrdering = orderByFavorites ? '-is_favorite,' : '';

  const { data, isSuccess, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProducts({
      searchTerm: search,
      type: 'FEES AND MISCELLANEOUS',
      ordering: `${favoriteOrdering}${ascending ? '' : '-'}${ordering}`,
    });

  const productsInCart = useMemo(() => cartItems.map(({ productId }) => productId), [cartItems]);
  const isItemInCart = (productId: string): boolean => productsInCart.includes(productId);

  const handleDelete = (product: ProductRetrieve): void => {
    setSelectedProduct(product);
    openDeleteModal();
  };

  const handleCloseDeleteModal = (): void => {
    setSelectedProduct(null);
    closeDeleteModal();
  };

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setAscending(!ascending);
      setOrdering(header);
    },
    [ascending],
  );

  const handleRowClick = (product: ProductRetrieve): void => {
    if (invoiceCreatePerm) {
      if (isItemInCart(product.productId)) {
        setCartItems(prev => prev.filter(({ productId: id }) => id !== product.productId));
      } else {
        setCartItems(prev => [...prev, { ...product }]);
      }
    }
  };

  const columns = [
    {
      Header: '',
      accessor: 'isFavorite',
      Cell: ({ value }: CellProps<ProductRetrieve>) =>
        value ? (
          <IconStarFilled style={{ color: theme.colors.yellow[5] }} size={theme.fontSizes.xl} />
        ) : (
          <></>
        ),
    },
    {
      Header: 'Item Name',
      accessor: 'name',
      Cell: ({ row }: CellProps<ProductRetrieve>) => (
        <Text weight='bold' truncate>
          {row.original.name}
        </Text>
      ),
      maxWidth: '200px',
    },
    {
      Header: 'Price',
      accessor: 'price',
      Cell: ({ value }: CellProps<ProductRetrieve>) => formatCurrency((value || 0) / 100),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<ProductRetrieve>) => {
        return (
          <Group position='right' px='xs'>
            <Menu
              position='bottom-end'
              items={[
                {
                  title: 'Edit',
                  onClick: () => {
                    setProductId(row.original.productId);
                    openDrawer();
                  },
                },
                {
                  title: 'Delete',
                  color: 'Red',
                  onClick: () => handleDelete(row.original),
                },
              ]}
              disabled={!productMutatePerm}
            />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [data, isLoading]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name', 'productPrice.price']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
        handleRowClick={handleRowClick}
        rowClassName={(product: ProductRetrieve) =>
          isItemInCart(product.productId) ? classes.tableRowSelected : classes.tableRow
        }
        bgc={bgc}
      />

      {selectedProduct && (
        <ModalFeesAndMiscProductsDelete
          opened={isDeleteModalOpened}
          handleClose={handleCloseDeleteModal}
          product={selectedProduct as ProductRetrieve}
          handleClearCart={() => setCartItems([])}
        />
      )}
    </>
  );
};

export default FeesAndMiscProductsTable;
